// src/hooks/usePerformance.js
// React hooks for performance optimization

import { useState, useCallback, useMemo, useRef, useEffect, useReducer } from 'react';
import {
  shallowEqual,
  createRenderProfiler,
  detectUnnecessaryRender,
  calculateVisibleItems,
  scheduleIdleUpdate,
  cancelIdleUpdate,
} from '../Utils/performanceUtils';

/**
 * useReducerState - Consolidate multiple useState into a single reducer
 * Reduces re-renders by batching state updates
 *
 * @param {Object} initialState - Initial state object
 * @returns {[Object, Function, Function]} [state, setState, resetState]
 *
 * @example
 * // Instead of:
 * const [loading, setLoading] = useState(false);
 * const [error, setError] = useState(null);
 * const [data, setData] = useState(null);
 *
 * // Use:
 * const [state, setState] = useReducerState({
 *   loading: false,
 *   error: null,
 *   data: null,
 * });
 *
 * // Update multiple values at once (single re-render):
 * setState({ loading: false, data: result, error: null });
 */
export function useReducerState(initialState) {
  const initialStateRef = useRef(initialState);

  const reducer = useCallback((state, action) => {
    if (action.type === 'RESET') {
      return initialStateRef.current;
    }
    if (action.type === 'SET') {
      return { ...state, ...action.payload };
    }
    return state;
  }, []);

  const [state, dispatch] = useReducer(reducer, initialState);

  const setState = useCallback((updates) => {
    dispatch({ type: 'SET', payload: updates });
  }, []);

  const resetState = useCallback(() => {
    dispatch({ type: 'RESET' });
  }, []);

  return [state, setState, resetState];
}

/**
 * useMemoCompare - useMemo with custom comparison function
 * Useful when you need deep comparison or custom logic
 *
 * @param {Function} factory - Factory function to compute value
 * @param {any} deps - Dependencies (not array, any value)
 * @param {Function} compare - Comparison function
 * @returns {any} Memoized value
 *
 * @example
 * const filteredData = useMemoCompare(
 *   () => expensiveFilter(data, filter),
 *   { data, filter },
 *   (prev, next) => prev.filter === next.filter && prev.data.length === next.data.length
 * );
 */
export function useMemoCompare(factory, deps, compare = shallowEqual) {
  const ref = useRef({ deps: undefined, value: undefined, initialized: false });

  if (!ref.current.initialized || !compare(ref.current.deps, deps)) {
    ref.current.deps = deps;
    ref.current.value = factory();
    ref.current.initialized = true;
  }

  return ref.current.value;
}

/**
 * useStableCallback - Create a stable callback that doesn't cause re-renders
 * but always has access to latest closure values
 *
 * @param {Function} callback
 * @returns {Function} Stable callback reference
 *
 * @example
 * // This callback reference never changes, safe to pass to memoized children
 * const handleClick = useStableCallback((id) => {
 *   // Always has access to latest state/props
 *   console.log(currentState, id);
 * });
 */
export function useStableCallback(callback) {
  const callbackRef = useRef(callback);

  // Update ref on each render
  useEffect(() => {
    callbackRef.current = callback;
  });

  // Return stable callback
  return useCallback((...args) => callbackRef.current(...args), []);
}

/**
 * useStableValue - Memoize a value with stable reference
 * Only updates when shallow comparison fails
 *
 * @param {any} value
 * @returns {any} Stable value reference
 */
export function useStableValue(value) {
  const ref = useRef(value);

  if (!shallowEqual(ref.current, value)) {
    ref.current = value;
  }

  return ref.current;
}

/**
 * usePrevious - Track previous value of a variable
 *
 * @param {any} value
 * @returns {any} Previous value
 */
export function usePrevious(value) {
  const ref = useRef();

  useEffect(() => {
    ref.current = value;
  }, [value]);

  return ref.current;
}

/**
 * useWhyDidYouUpdate - Debug hook to see why a component re-rendered
 * Only logs in development mode
 *
 * @param {string} name - Component name
 * @param {Object} props - Current props
 */
export function useWhyDidYouUpdate(name, props) {
  const previousProps = useRef(props);

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      const allKeys = new Set([
        ...Object.keys(previousProps.current || {}),
        ...Object.keys(props),
      ]);

      const changes = {};
      let hasChanges = false;

      allKeys.forEach((key) => {
        if (previousProps.current?.[key] !== props[key]) {
          hasChanges = true;
          changes[key] = {
            from: previousProps.current?.[key],
            to: props[key],
          };
        }
      });

      if (hasChanges) {
        console.log(`[WhyDidYouUpdate] ${name}:`, changes);
      }
    }

    previousProps.current = props;
  });
}

/**
 * useRenderCount - Track render count for debugging
 *
 * @param {string} componentName
 * @returns {number} Render count
 */
export function useRenderCount(componentName) {
  const countRef = useRef(0);
  countRef.current++;

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log(`[RenderCount] ${componentName}: ${countRef.current}`);
    }
  });

  return countRef.current;
}

/**
 * useRenderProfiler - Profile component render performance
 *
 * @param {string} componentName
 * @returns {Object} Profiler metrics
 */
export function useRenderProfiler(componentName) {
  const profilerRef = useRef(createRenderProfiler(componentName));

  // Start timing at render
  profilerRef.current.start();

  // End timing after render
  useEffect(() => {
    profilerRef.current.end();
  });

  return profilerRef.current.getMetrics();
}

/**
 * useDeferredValue - Defer a value update to avoid blocking renders
 * Polyfill for React 18's useDeferredValue
 *
 * @param {any} value
 * @param {number} delay - Defer delay in ms
 * @returns {any} Deferred value
 */
export function useDeferredValue(value, delay = 100) {
  const [deferredValue, setDeferredValue] = useState(value);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDeferredValue(value);
    }, delay);

    return () => clearTimeout(timeoutId);
  }, [value, delay]);

  return deferredValue;
}

/**
 * useIdleCallback - Run callback when browser is idle
 *
 * @param {Function} callback
 * @param {Object} options
 */
export function useIdleCallback(callback, options = {}) {
  const { timeout = 1000, deps = [] } = options;
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    const id = scheduleIdleUpdate(() => callbackRef.current(), { timeout });
    return () => cancelIdleUpdate(id);
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
}

/**
 * useVirtualList - Virtualize a long list for performance
 *
 * @param {Object} options
 * @returns {Object} Virtualization helpers
 *
 * @example
 * const { visibleItems, containerProps, listProps } = useVirtualList({
 *   items: largeArray,
 *   itemHeight: 50,
 *   containerHeight: 400,
 * });
 *
 * return (
 *   <div {...containerProps}>
 *     <div {...listProps}>
 *       {visibleItems.map(item => <Row key={item.id} item={item} />)}
 *     </div>
 *   </div>
 * );
 */
export function useVirtualList(options) {
  const {
    items,
    itemHeight,
    containerHeight,
    overscan = 3,
  } = options;

  const [scrollTop, setScrollTop] = useState(0);
  const containerRef = useRef(null);

  const handleScroll = useCallback((e) => {
    setScrollTop(e.target.scrollTop);
  }, []);

  const { startIndex, endIndex, offsetTop, totalHeight } = useMemo(() => {
    return calculateVisibleItems({
      scrollTop,
      containerHeight,
      itemHeight,
      totalItems: items.length,
      overscan,
    });
  }, [scrollTop, containerHeight, itemHeight, items.length, overscan]);

  const visibleItems = useMemo(() => {
    return items.slice(startIndex, endIndex + 1).map((item, index) => ({
      ...item,
      _virtualIndex: startIndex + index,
    }));
  }, [items, startIndex, endIndex]);

  const containerProps = {
    ref: containerRef,
    onScroll: handleScroll,
    style: {
      height: containerHeight,
      overflow: 'auto',
    },
  };

  const listProps = {
    style: {
      height: totalHeight,
      position: 'relative',
    },
  };

  const itemProps = {
    style: {
      position: 'absolute',
      top: offsetTop,
      width: '100%',
    },
  };

  return {
    visibleItems,
    containerProps,
    listProps,
    itemProps,
    scrollTo: (index) => {
      if (containerRef.current) {
        containerRef.current.scrollTop = index * itemHeight;
      }
    },
  };
}

/**
 * useIntersectionObserver - Lazy load content when visible
 *
 * @param {Object} options
 * @returns {[RefObject, boolean]} [ref, isVisible]
 *
 * @example
 * const [ref, isVisible] = useIntersectionObserver({ threshold: 0.1 });
 *
 * return (
 *   <div ref={ref}>
 *     {isVisible ? <ExpensiveComponent /> : <Placeholder />}
 *   </div>
 * );
 */
export function useIntersectionObserver(options = {}) {
  const { threshold = 0, rootMargin = '0px', triggerOnce = false } = options;

  const [isVisible, setIsVisible] = useState(false);
  const elementRef = useRef(null);
  const observerRef = useRef(null);

  useEffect(() => {
    if (!('IntersectionObserver' in window)) {
      setIsVisible(true);
      return;
    }

    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        const visible = entry.isIntersecting;
        setIsVisible(visible);

        if (visible && triggerOnce && observerRef.current && elementRef.current) {
          observerRef.current.unobserve(elementRef.current);
        }
      },
      { threshold, rootMargin }
    );

    if (elementRef.current) {
      observerRef.current.observe(elementRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [threshold, rootMargin, triggerOnce]);

  return [elementRef, isVisible];
}

/**
 * useDebounce - Debounce state updates
 *
 * @param {any} value
 * @param {number} delay
 * @returns {any} Debounced value
 */
export function useDebounce(value, delay = 300) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

/**
 * useThrottle - Throttle state updates
 *
 * @param {any} value
 * @param {number} limit
 * @returns {any} Throttled value
 */
export function useThrottle(value, limit = 300) {
  const [throttledValue, setThrottledValue] = useState(value);
  const lastRan = useRef(Date.now());

  useEffect(() => {
    const handler = setTimeout(() => {
      if (Date.now() - lastRan.current >= limit) {
        setThrottledValue(value);
        lastRan.current = Date.now();
      }
    }, limit - (Date.now() - lastRan.current));

    return () => {
      clearTimeout(handler);
    };
  }, [value, limit]);

  return throttledValue;
}

/**
 * useUpdateEffect - useEffect that skips the first render
 *
 * @param {Function} effect
 * @param {Array} deps
 */
export function useUpdateEffect(effect, deps) {
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    return effect();
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps
}

/**
 * useIsFirstRender - Check if this is the first render
 *
 * @returns {boolean}
 */
export function useIsFirstRender() {
  const isFirst = useRef(true);

  if (isFirst.current) {
    isFirst.current = false;
    return true;
  }

  return false;
}

/**
 * useMountedState - Track if component is mounted
 * Useful for async operations
 *
 * @returns {Function} isMounted function
 */
export function useMountedState() {
  const mountedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  return useCallback(() => mountedRef.current, []);
}

/**
 * useForceUpdate - Force a component re-render
 *
 * @returns {Function} forceUpdate function
 */
export function useForceUpdate() {
  const [, setTick] = useState(0);
  return useCallback(() => setTick(tick => tick + 1), []);
}

/**
 * useEventCallback - Event callback that's always stable but uses latest values
 *
 * @param {Function} fn
 * @returns {Function}
 */
export function useEventCallback(fn) {
  const ref = useRef(fn);
  ref.current = fn;

  return useCallback((...args) => {
    return ref.current(...args);
  }, []);
}

export default {
  useReducerState,
  useMemoCompare,
  useStableCallback,
  useStableValue,
  usePrevious,
  useWhyDidYouUpdate,
  useRenderCount,
  useRenderProfiler,
  useDeferredValue,
  useIdleCallback,
  useVirtualList,
  useIntersectionObserver,
  useDebounce,
  useThrottle,
  useUpdateEffect,
  useIsFirstRender,
  useMountedState,
  useForceUpdate,
  useEventCallback,
};
